import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@repo/ui";
import { useState } from "react";

import { fmContent } from "@/content/farmers-market";

function MilkBottleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 64 64" aria-hidden="true" {...props}>
      <g
        fill="none"
        stroke="var(--brown)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M24 10h16l-2 8H26l-2-8Z" />
        <path d="M26 18h12c2 7 6 12 6 20 0 10-5 16-12 16s-12-6-12-16c0-8 4-13 6-20Z" />
      </g>
    </svg>
  );
}

function StampIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 64 64" aria-hidden="true" {...props}>
      <rect
        x="6"
        y="6"
        width="52"
        height="52"
        rx="6"
        fill="none"
        stroke="var(--brown)"
        strokeWidth="2"
      />
      <path d="M12 20h40M12 44h40" stroke="var(--brown)" strokeWidth="2" />
    </svg>
  );
}

export default function FarmersMarketPage() {
  const [open, setOpen] = useState(false);

  return (
    <div className="theme-farmers min-h-full" style={{ background: "var(--cream)" }}>
      {/* Top Nav */}
      <header className="sticky top-0 z-50 border-b" style={{ borderColor: "var(--brown)" }}>
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <a
            href="#"
            className="inline-flex items-center gap-3"
            aria-label="Local Farmers Market home"
          >
            <span
              className="px-4 py-1 rounded-full border text-sm"
              style={{ borderColor: "var(--brown)", color: "var(--brown)" }}
            >
              Local Farmers Market
            </span>
          </a>
          <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
            {fmContent.nav.links.map((l) => (
              <a key={l.href} href={l.href} className="text-sm" style={{ color: "var(--brown)" }}>
                {l.label}
              </a>
            ))}
          </nav>
          <a
            href="#support"
            role="button"
            aria-label="Work With Us"
            className="px-5 py-2 rounded-full text-white text-sm"
            style={{ background: "var(--olive)" }}
          >
            {fmContent.nav.cta}
          </a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative">
          <div className="absolute inset-0 -z-10">
            <div
              className="h-full w-full"
              style={{
                backgroundImage: `repeating-linear-gradient(0deg, var(--blue-100), var(--blue-100) 24px, #ffffff00 24px, #ffffff00 48px)`,
              }}
            />
          </div>
          <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-10 items-center">
            {/* Left Image Panel */}
            <div className="relative">
              <div
                className="rounded-[24px] p-2"
                style={{ background: "white", boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }}
              >
                <div
                  className="aspect-[4/5] w-full rounded-[20px] bg-[#e9e9e9] grid place-items-center text-sm text-neutral-500"
                  aria-label="Smiling vendor with fresh produce"
                  role="img"
                >
                  Hero Image Placeholder
                </div>
              </div>
              <div className="absolute -bottom-3 -right-3">
                <span
                  className="inline-block rounded-full px-3 py-2 text-xs font-medium"
                  style={{
                    background: "var(--blue)",
                    color: "white",
                    boxShadow: "0 6px 0 var(--brown)",
                  }}
                >
                  {fmContent.hero.badge}
                </span>
              </div>
            </div>

            {/* Right Copy */}
            <div>
              <h1
                className="text-4xl md:text-5xl font-bold mb-4"
                style={{
                  fontFamily: "'Playfair Display', ui-serif, serif",
                  color: "var(--brown)",
                  lineHeight: 1.2,
                }}
              >
                {fmContent.hero.title}
              </h1>
              <p
                className="mb-6 max-w-prose"
                style={{ fontFamily: "Inter, ui-sans-serif", color: "#3f3f3f", lineHeight: 1.8 }}
              >
                {fmContent.hero.body}
              </p>

              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="rounded-full"
                    variant="outline"
                    aria-label="View Schedule"
                    onClick={() => setOpen(true)}
                    style={{ borderColor: "var(--blue)", color: "var(--blue)" }}
                  >
                    {fmContent.hero.cta}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{fmContent.schedule.title}</DialogTitle>
                  </DialogHeader>
                  <ul className="list-disc pl-5 space-y-2 text-sm">
                    {fmContent.schedule.items.map((i) => (
                      <li key={i}>{i}</li>
                    ))}
                  </ul>
                </DialogContent>
              </Dialog>

              <div className="relative mt-8 flex items-center gap-6">
                <MilkBottleIcon width={48} height={48} />
                <StampIcon width={52} height={52} />
              </div>
            </div>
          </div>
        </section>

        {/* Terracotta Ribbon */}
        <section className="py-3" style={{ background: "var(--terra)" }}>
          <p className="text-center text-white text-sm px-6">{fmContent.ribbon}</p>
        </section>

        {/* Mission Cards */}
        <section id="about" className="py-16" style={{ background: "var(--terra)" }}>
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-6">
            {fmContent.mission.map((m) => (
              <div
                key={m.title}
                className="p-6 rounded-[24px] border"
                style={{
                  background: "var(--sage)",
                  borderColor: "var(--brown)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                }}
              >
                <h3 className="text-xl font-semibold mb-2" style={{ color: "var(--olive)" }}>
                  {m.title}
                </h3>
                <p className="text-sm" style={{ color: "var(--brown)", lineHeight: 1.8 }}>
                  {m.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Support */}
        <section id="support" className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2
              className="text-3xl md:text-4xl font-bold mb-10"
              style={{ fontFamily: "'Playfair Display', ui-serif, serif", color: "var(--brown)" }}
            >
              {fmContent.support.heading}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {fmContent.support.cards.map((c) => (
                <div
                  key={c.title}
                  className="p-6 rounded-[24px] border"
                  style={{ background: "var(--blue-100)", borderColor: "var(--blue)" }}
                >
                  <h3 className="text-lg font-semibold mb-2" style={{ color: "var(--olive)" }}>
                    {c.title}
                  </h3>
                  <ul
                    className="list-disc pl-5 space-y-1 text-sm"
                    style={{ color: "var(--brown)" }}
                  >
                    {c.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: "var(--brown)" }}>
        <div className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-6 items-start">
          <div>
            <p className="text-sm" style={{ color: "var(--brown)" }}>
              {fmContent.footer.address}
            </p>
          </div>
          <nav className="flex gap-4" aria-label="Social">
            {fmContent.footer.socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                className="text-sm underline"
                style={{ color: "var(--brown)" }}
              >
                {s.label}
              </a>
            ))}
          </nav>
          <p className="text-xs md:text-right" style={{ color: "var(--brown)" }}>
            {fmContent.footer.small}
          </p>
        </div>
      </footer>
    </div>
  );
}
